import { useEffect, useLayoutEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { X } from "lucide-react";
import { API } from "@/portal/AuthContext";
import { eur } from "@/components/PaketPreis";
import { useAktiveAktion, aktionGilt } from "@/lib/useAktion";
import { isEmbed } from "@/components/tickets/Embed";

const YELLOW = "#FFD60A";
const INK = "#0B0B0B";
const ANTON = { fontFamily: "'Anton', sans-serif" };
const POP = { fontFamily: "'Poppins', sans-serif" };

// Banner nur auf Marketing-Seiten; auf diesen Routen gar nicht zeigen (Editoren, Admin, öffentliche Kurzlinks).
const VERSTECKT = /^\/(admin|link-in-bio|baukasten|studio-editor|studio-guthaben|upload|passwort|zahlung|k|t|v|s|karte|l)(\/|$)/;
const PORTAL = /^\/(app|portal)(\/|$)/;

// Datum ohne Jahr: "17.11.2026" -> "17.11."
const kurz = (d) => { const p = (d || "").split("."); return p.length >= 2 ? `${p[0]}.${p[1]}.` : (d || ""); };

const STYLE_ID = "bw-nav-offset";
// Fixierte Header (öffentliche Navi + Portal-Topbar) um die aktuell sichtbare Bannerhöhe nach unten schieben.
// Das Banner selbst liegt im normalen Fluss → der Seiteninhalt bekommt seinen Abstand automatisch und verliert ihn beim Schließen.
function useHeaderOffset(ref, active) {
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (!active) { root.style.setProperty("--bw-push", "0px"); return; }
    let style = document.getElementById(STYLE_ID);
    if (!style) {
      style = document.createElement("style");
      style.id = STYLE_ID;
      style.textContent = `header.site-nav,[data-testid="portal-topbar"]{top:var(--bw-push,0px)!important;transition:top .12s linear}`;
      document.head.appendChild(style);
    }
    const update = () => {
      const h = ref.current ? ref.current.offsetHeight : 0;
      root.style.setProperty("--bw-push", `${Math.max(0, h - window.scrollY)}px`);
    };
    update();
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const ro = ref.current ? new ResizeObserver(update) : null;
    if (ro && ref.current) ro.observe(ref.current);
    return () => {
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      if (ro) ro.disconnect();
      root.style.setProperty("--bw-push", "0px");
    };
  }, [ref, active]);
}

const Keyframes = () => (
  <style>{`
@keyframes bwbob{0%,100%{transform:rotate(-8deg) translateY(0)}50%{transform:rotate(-8deg) translateY(-5px)}}
@keyframes bwwave{0%,100%{transform:rotate(-6deg)}50%{transform:rotate(-11deg)}}
.bw-sticker{animation:bwbob 2.6s ease-in-out infinite}
.bw-robbie{transform-origin:bottom center;animation:bwwave 2.9s ease-in-out infinite}
@media (prefers-reduced-motion: reduce){.bw-sticker{animation:none !important;transform:rotate(-8deg)}.bw-robbie{animation:none !important;transform:rotate(-6deg)}}
`}</style>
);

export const BlackWeeksBanner = () => {
  const { pathname, search } = useLocation();
  const { aktion } = useAktiveAktion();
  const [dismissed, setDismissed] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!aktion) return;
    try { setDismissed(window.localStorage.getItem(`dimedial-bw-${aktion.id}`) === "1"); } catch { /* ignore */ }
  }, [aktion]);

  // Nie auf Kundenseiten (Mini-Websites /@handle) oder in eingebetteten (embed/iframe) Ansichten.
  const versteckt = !aktion || VERSTECKT.test(pathname) || pathname.startsWith("/@") || isEmbed(search);
  const istPortal = PORTAL.test(pathname);
  const showPortal = !versteckt && istPortal && !!aktion?.berechtigt;
  const showPublic = !versteckt && !istPortal && !dismissed;
  useHeaderOffset(rootRef, showPortal || showPublic);

  if (showPortal) {
    return (
      <div ref={rootRef} className="relative w-full" style={{ background: INK, borderBottom: `3px solid ${YELLOW}` }} data-testid="blackweeks-status">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-center gap-x-3 gap-y-1 px-4 py-2 text-center">
          <span style={{ ...ANTON, color: YELLOW }} className="text-base md:text-lg tracking-wide">BLACK WEEKS</span>
          <span style={POP} className="text-xs md:text-sm text-white">Jetzt buchen &amp; {aktion.rabatt_prozent} % auf {aktion.dauer_monate} Monate sparen{aktion.tage_bis_ende ? ` – noch ${aktion.tage_bis_ende} Tage` : ""}.</span>
          <a href="/portal#preise" data-testid="blackweeks-status-cta" style={{ ...POP, background: YELLOW }} className="rounded-full px-3 py-1 text-xs font-bold text-black">Zu den Preisen →</a>
        </div>
      </div>
    );
  }

  if (!showPublic) return null;

  const dismiss = () => { try { window.localStorage.setItem(`dimedial-bw-${aktion.id}`, "1"); } catch { /* ignore */ } setDismissed(true); };
  const buchen = aktion.angemeldet && aktion.berechtigt;
  const href = buchen ? "/portal#preise" : "/kunden/registrieren";
  const ctaText = buchen ? "Jetzt Paket buchen →" : "30 Tage gratis testen →";
  const pz = `−${aktion.rabatt_prozent}%`;

  return (
    <div ref={rootRef} className="relative w-full overflow-hidden" style={{ background: INK, borderBottom: `4px solid ${YELLOW}` }} data-testid="blackweeks-banner">
      <Keyframes />
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center whitespace-nowrap" style={{ opacity: 0.07 }}>
        <span style={{ ...ANTON, color: "#fff", fontSize: "120px", letterSpacing: "0.12em" }}>{pz}&nbsp;&nbsp;{pz}&nbsp;&nbsp;{pz}&nbsp;&nbsp;{pz}&nbsp;&nbsp;{pz}</span>
      </div>
      <button type="button" onClick={dismiss} aria-label="Black-Weeks-Hinweis schließen" data-testid="blackweeks-close"
        className="absolute right-3 top-3 z-30 grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white/80 hover:text-white hover:bg-white/20 transition-colors">
        <X size={20} />
      </button>
      <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center justify-center gap-4 px-4 py-5 text-center md:flex-row md:gap-7 md:py-4 md:text-left" style={{ minHeight: "150px" }}>
        <div className="relative shrink-0">
          <img src="/img/mascots/robbie-winkt.webp" alt="Robbie winkt" className="bw-robbie h-24 w-auto md:h-28" />
          <span className="absolute -top-2 -right-4 rotate-6 rounded-lg px-2 py-1 text-[11px] font-bold" style={{ background: YELLOW, color: "#000", border: "2px solid #000", ...POP }}>Psst – halber Preis!</span>
        </div>
        <div className="shrink-0">
          <p style={{ ...ANTON, color: "#fff" }} className="text-4xl md:text-[56px] leading-none tracking-wide">BLACK WEEKS</p>
          <p style={POP} className="mt-1 text-xs md:text-sm text-white/55">{kurz(aktion.start_datum)} – {kurz(aktion.ende_datum)}</p>
        </div>
        <div className="bw-sticker flex shrink-0 flex-col items-center justify-center rounded-full text-center" style={{ width: 94, height: 94, background: YELLOW, border: "3px solid #fff" }}>
          <span style={{ ...ANTON, color: "#000" }} className="text-4xl leading-none">{pz}</span>
          <span style={POP} className="text-[10px] font-bold text-black">{aktion.dauer_monate} MONATE</span>
        </div>
        <div className="md:flex-1">
          <p style={POP} className="text-lg font-extrabold leading-snug text-white md:text-2xl">{aktion.banner_text}</p>
          <p style={POP} className="mt-1 text-xs leading-snug text-white/55 md:text-sm">{aktion.preis_kurztext}</p>
        </div>
        <a href={href} data-testid="blackweeks-cta" style={{ background: YELLOW, border: "2px solid #fff", boxShadow: "0 4px 0 #fff", ...POP }}
          className="w-full shrink-0 rounded-full px-5 py-3 text-center text-sm font-extrabold text-black transition-transform duration-200 hover:scale-[1.03] md:w-auto">{ctaText}</a>
      </div>
    </div>
  );
};

// Aktionskarte im Kundenbereich (/portal#preise): zeigt die Monatstarife mit altem/neuem Preis – nur für berechtigte Konten.
export const BlackWeeksPreisKarte = () => {
  const { aktion } = useAktiveAktion();
  const [k, setK] = useState(null);
  useEffect(() => { if (aktion) axios.get(`${API}/pakete`).then((r) => setK(r.data)).catch(() => setK(null)); }, [aktion]);
  if (!aktion || !aktion.berechtigt || !k || k.fehler) return null;
  const tarife = (k.pakete || []).filter((p) => aktionGilt(aktion, p.code, "monat"));
  if (!tarife.length) return null;
  const f = (n) => n * (1 - aktion.rabatt_prozent / 100);
  return (
    <div className="rounded-2xl border-2 p-4" style={{ background: INK, borderColor: YELLOW }} data-testid="portal-blackweeks-karte">
      <div className="flex flex-wrap items-center gap-2">
        <span style={{ ...ANTON, color: YELLOW }} className="text-xl tracking-wide">BLACK WEEKS</span>
        <span className="rounded-full px-2 py-0.5 text-xs font-bold text-black" style={{ background: YELLOW }}>−{aktion.rabatt_prozent} % · {aktion.dauer_monate} Monate</span>
      </div>
      <p style={POP} className="mt-1 text-sm text-white/80">{aktion.banner_text} {aktion.preis_kurztext}</p>
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        {tarife.map((p) => (
          <div key={p.code} className="rounded-xl bg-white/5 p-3" data-testid={`portal-bw-tarif-${p.code}`}>
            <p style={POP} className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/50">{p.name}</p>
            <p style={POP} className="mt-1 text-white"><span className="mr-1.5 text-sm text-white/40 line-through">{eur(p.preis)}</span><span className="text-xl font-extrabold" style={{ color: YELLOW }}>{eur(f(p.preis))}</span> <span className="text-xs text-white/60">/ Monat netto</span></p>
            <p style={POP} className="mt-0.5 text-[11px] text-white/45">für {aktion.dauer_monate} Monate · danach {eur(p.preis)} netto/Monat</p>
          </div>
        ))}
      </div>
    </div>
  );
};
